import * as React from 'react'
import { Image, StyleSheet, TouchableOpacity, Dimensions, Vibration, View , Platform} from 'react-native'
import { Audio, AVPlaybackStatus } from 'expo-av'

import PlayerControls from './PlayerControls'
import Screen from '../../components/Screen'
import { Text } from '../../components/Themed'
import { useAppSelector, useMeditation } from '../../hooks'
import NotFoundScreen from '../NotFoundScreen'
import { HomeParamList, MainStackParamList } from '../../types'
import { CompositeNavigationProp, RouteProp, useNavigationContainerRef } from '@react-navigation/native'
import { useMsToTime, useAppDispatch } from '../../hooks'
import { completed, updateFavourite } from '../../redux/meditationSlice'
import { LoadingScreen } from '../../components'
import { useCallback } from 'react'
import { StackNavigationProp } from '@react-navigation/stack'
import { selectFavourites, selectFilePaths } from '../../redux/selectors'
import FavouriteButton from '../../components/FavouriteButton'
import { Meditation } from '../../data/meditations'

type PlayRouteProp = RouteProp<HomeParamList, 'FieldScreen'>

type PlayNavProp = CompositeNavigationProp<
  StackNavigationProp<HomeParamList, 'FieldScreen'>,
  StackNavigationProp<MainStackParamList>
>
interface Props {
  navigation: PlayNavProp
  route: PlayRouteProp
}
export default function FieldScreen({ route, navigation }: Props) {
  const { id } = route.params
  const meditation = useMeditation(id)
  const favourites = useAppSelector(selectFavourites)
  const [isLoadingAudio, setIsLoadingAudio] = React.useState(true)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [isRunning, setIsRunning] = React.useState(false)
  const [remainingSeconds, setRemainingSeconds] = React.useState(60)
  const [sound, setSound] = React.useState<Audio.Sound>()
  const [positionMillis, setPositionMillis] = React.useState(0)
  const [durationMills, setDurationMills] = React.useState(0)
  const durationTime = useMsToTime(durationMills)
  const positionTime = useMsToTime(positionMillis)
  const dispatch = useAppDispatch()
  const uri = meditation?.uri || ''
  const filepaths = useAppSelector(selectFilePaths)

  //let interval: NodeJS.Timeout

  const _interval = React.useRef(null);
  const interval = () => _interval.current;
  
  const _totalSeconds = React.useRef(null);
  const totalSeconds = () => _totalSeconds.current;

  const isFavourited = favourites.some((item: Meditation) => item.id === meditation?.id)

  const onFavourite = () => {
    dispatch(updateFavourite(meditation))
  }

  const onPlaybackStatusUpdate = useCallback(
    (playbackStatus: AVPlaybackStatus) => {
      if (!playbackStatus.isLoaded) {
        // Update your UI for the unloaded state
      } else {
        // Update your UI for the loaded state
        if (playbackStatus.positionMillis) {
          setPositionMillis(playbackStatus.positionMillis)
        }
        if (playbackStatus.durationMillis) {
          setDurationMills(playbackStatus.durationMillis)
        }
        if (playbackStatus.didJustFinish) {
          //setIsPlaying(false)
          //sound?.setPositionAsync(0)
          //sound?.stopAsync()
        }
      }
    },
    [dispatch, navigation]
  )

  React.useEffect(() => {
    return sound
      ? () => {
          sound.unloadAsync()
        }
      : undefined
  }, [sound])

  React.useEffect(() => {
    const loadAudio = async () => {
      let filename = uri.split('/').pop() ?? ''
      let filepath = filepaths.find((file) => {
        if (file.split('/').pop() === filename) {
          return file
        }
      })

      setIsLoadingAudio(true)

      await Audio.setAudioModeAsync({
        playsInSilentModeIOS: true,
        staysActiveInBackground: true,
      })

      if (filepath) {
        // Load from downloaded audio file
        const _sound = new Audio.Sound()
        _sound.setOnPlaybackStatusUpdate(onPlaybackStatusUpdate)
        await _sound.loadAsync({ uri: filepath })
        setSound(_sound)
      } else {
        // Load from remote URI
        const { sound: _sound } = await Audio.Sound.createAsync({ uri }, {}, onPlaybackStatusUpdate)
        setSound(_sound)
      }

      setIsLoadingAudio(false)
    }

    loadAudio()
  }, [onPlaybackStatusUpdate, uri, filepaths])

  const replay = async () => {
    await sound?.setPositionAsync(positionMillis - 10 * 1000)
  }

  const forward = async () => {
    await sound?.setPositionAsync(positionMillis + 10 * 1000)
  }

  const play = async () => {
    if (sound) {
      //await sound.playAsync()
      await sound.replayAsync()
      setIsPlaying(true)
    }
  }

  const pause = async () => {
    if (sound) {
      await sound.pauseAsync()
      setIsPlaying(false)
    }
  }

  if (!meditation) {
    return <NotFoundScreen />
  }

  const { title, subtitle, image } = meditation
  
  const formatNumber = (number: number) => `0${number}`.slice(-2);

  const getRemaining = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time - minutes * 60;
    return { minutes: formatNumber(minutes), seconds: formatNumber(seconds) };
  };
  const getElapsed = (time: number) => {
    if (time < 0) time = 0
    const elapsedMinutes = Math.floor(time / 60);
    const elapsedSeconds = time - elapsedMinutes * 60;
    return { elapsedMinutes: formatNumber(elapsedMinutes), elapsedSeconds: formatNumber(elapsedSeconds) };
  };

  const { minutes, seconds } = getRemaining(remainingSeconds);
  
  const { elapsedMinutes, elapsedSeconds } = getElapsed(_totalSeconds.current);

  if (isLoadingAudio) {
    return <LoadingScreen loading={isLoadingAudio} />
  }

  const start = () => {
      setRemainingSeconds(60)      
      setIsRunning(true)
      if (_totalSeconds.current == null || _totalSeconds.current == -1) {
        _totalSeconds.current = 0
        _interval.current = setInterval(() => {   
          if (navigation.getState().index == 0) {
            clearInterval(_interval.current)
          }
          _totalSeconds.current = _totalSeconds.current + 1
          setRemainingSeconds(
            remainingSeconds =>{
              checkCounter(remainingSeconds - 1)
              return remainingSeconds - 1;
          })
        }, 1000)    
      }
  };

  const checkCounter = (remainingSeconds: number) => {
    if (remainingSeconds <= 0) {
      play()

      dispatch(completed(totalSeconds() * 1000))
      _totalSeconds.current = -1

      clearInterval(_interval.current)
      
      if (Platform.OS !== 'web') {
       Vibration.vibrate(1 * 256)
      }
      stop()
    }   
  };

  const stop = () => {
      //clearInterval(_interval.current)
      setRemainingSeconds(60)
      setIsRunning(false)
  };

  const restart = () => {
    stop();
    start();
  }

  return (
    <View style={styles.container}>
      <FavouriteButton isFavourited={isFavourited} style={styles.favourite} onPress={onFavourite} />
      <Text style={styles.title}>Finger Induced Lucid Dream</Text>
      <Image source={image} style={styles.image} />
      <Text style={styles.timerText}>{`${minutes}:${seconds}`}</Text>
      <Text style={styles.timeElapsedText}>{`${elapsedMinutes}:${elapsedSeconds}`}</Text>
        {isRunning ? (
          <TouchableOpacity
            onPress={restart}
            style={[styles.button, styles.buttonStop]}
          >
            <Text style={[styles.buttonText, styles.buttonTextStop]}>Awake</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity onPress={restart} style={styles.button}>
            <Text style={styles.buttonText}>Start</Text>
          </TouchableOpacity>
        )}
    </View>
  )
}

const screen = Dimensions.get("window");

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingLeft: 32,
    paddingRight: 32,
  },
  title: {
    fontSize: 18,
    textAlign: 'center',
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 20,
  },
  subtitle: {
    textAlign: 'center',
    fontSize: 16,
    marginBottom: 20,
  },
  image: {
    width: '100%',
    height: '100%',
    maxWidth: 300,
    maxHeight: 300,
    marginBottom: 10,
    borderRadius: 10,
    alignSelf: 'center',
  },
  favourite: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  nav: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems:'center',
    width: 1000,
    borderBottomWidth: 2,
    flexWrap : 'wrap',
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 1000,
  },
  timerText: {
    textAlign: 'center',
    fontSize: 48
  },
  timeElapsedText: {
    textAlign: 'center',
    fontSize: 20
  },
  button: {
    borderWidth: 4,
    borderColor: "#4682B4",
    width: '90%',
    height: 160,
    borderRadius: 50,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    marginTop: 10
  },
  buttonStop: {
    borderColor: "#FF851B"
  },
  buttonText: {
    justifyContent: "center",
    textAlign: 'center',
    fontSize: 32,
    color: "#4682B4"
  },
  buttonTextStop: {
    color: "#FF851B"
  },
})
